import { useEffect, useState } from "react";
import type { ForteXApi, UserRoles } from "@fortex/api";
import { Skeleton, Switch, errorMessage } from "@fortex/map";
import { useApp } from "../../app/context";
import type { AppLabels } from "../../i18n";

export type UserRoleSwitchesProps = {
  api: ForteXApi;
  labels: AppLabels;
  userId: string;
  readOnly: boolean;
};

export function UserRoleSwitches({ api, labels, userId, readOnly }: UserRoleSwitchesProps) {
  const { toast } = useApp();
  const [roles, setRoles] = useState<UserRoles | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    api
      .userRoles(userId, controller.signal)
      .then(result => {
        if (!controller.signal.aborted) setRoles(result);
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [api, userId]);

  const toggleRole = async (roleId: number, assigned: boolean) => {
    if (roles === null) return;

    const next = roles.roles.map(a => (a.roleId === roleId ? { ...a, assigned } : a));

    setBusy(true);

    try {
      const saved = await api.setUserRoles(userId, { roleIds: next.filter(a => a.assigned).map(a => a.roleId) });

      setRoles(saved);
      toast("success", labels.rolesSaved);
    } catch (reason) {
      toast("danger", errorMessage(reason, labels));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fxapp-role-list" data-fxmap="user-roles">
      {roles === null ? <Skeleton lines={3} /> : roles.roles.length === 0 ? <p className="fxmap-muted">{labels.noRoles}</p> : roles.roles.map(role => <Switch key={role.roleId} checked={role.assigned} disabled={busy || readOnly} onChange={assigned => void toggleRole(role.roleId, assigned)} label={role.roleName} showLabel />)}
    </div>
  );
}
